import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Skeleton } from '@mui/material';
import { useStatsigClient } from '@statsig/react-bindings';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import alertCircle from '../../../../assets/icons/v3/alert-circle.svg?url';
import calendar from '../../../../assets/icons/v3/calendar-dots.svg?url';
import BdsLabelPill from '../../../../shared/components/v3/BdsLabelPill/BdsLabelPill';
import CustomModal from '../../../../shared/components/v3/Modals/CustomModal/CustomModal';
import { BENEFIT_DETAIL_ROUTE, INSURANCE_DETAIL_ROUTE } from '../../../../shared/constants/auth';
import { BDS_PICTOGRAM_PATHS } from '../../../../shared/constants/bdsPictograms';
import {
  ACTIVATE_MARKETCARE_LINK,
  BENEFIT_PROVIDER_STATUS_DATA,
  MARKETCARE_INSURANCE_DESCRIPTION_KEY
} from '../../../../shared/constants/benefits';
import { CAN_SEE_COLLABORATOR_INSURANCE_START_DATE_FG } from '../../../../shared/constants/statsig';
import { CLICK_ACTION, COLLABORATOR_BENEFIT_DETAIL_MY_PLAN_DETAIL } from '../../../../shared/constants/tagManager';
import { CompanyContext } from '../../../../shared/context/company/companyContext';
import { PlansContext } from '../../../../shared/context/plans/plansContext';
import { ICollaboratorBenefit, IMembersData } from '../../../../shared/interfaces/businessData';
import { IButtonImageV3 } from '../../../../shared/interfaces/button';
import { ICollaboratorInsuranceDetail } from '../../../../shared/interfaces/collaborators';
import Companies from '../../../../shared/services/api/collaborators/collaborators';
import TagManagerService from '../../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_COLLABORATORS } from '../../../../shared/translations/collaborators';
import { BdsLabelPillVariant } from '../../../../shared/types/types';
import { isEmptyObject } from '../../../../shared/utils/validation/validation';

import './BenefitDetailModal.scss';

export interface IBenefitDetailModal {
  benefit: ICollaboratorBenefit;
  collaborator: IMembersData;
  isOpen: boolean;
  onClose: () => void;
}

const BenefitDetailModal = ({ benefit, collaborator, isOpen, onClose }: IBenefitDetailModal) => {
  const [statusIcon, setStatusIcon] = useState<IButtonImageV3>();
  const [tagVariant, setTagVariant] = useState<BdsLabelPillVariant>('info');
  const [description, setDescription] = useState<string | React.ReactElement>('');
  const [hasValidStatus, setHasValidStatus] = useState<boolean>(false);
  const [insuranceDetail, setInsuranceDetail] = useState<ICollaboratorInsuranceDetail>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const { assingSelectedBenefit } = useContext(PlansContext);
  const { company } = useContext(CompanyContext);
  const { client } = useStatsigClient();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const showStartDate = useMemo(() => {
    if (benefit.status !== 'accepted') return false;
    return client?.checkGate(CAN_SEE_COLLABORATOR_INSURANCE_START_DATE_FG);
  }, [client, benefit]);

  const redirectToDetail = useCallback(() => {
    TagManagerService.pushEvent(CLICK_ACTION, COLLABORATOR_BENEFIT_DETAIL_MY_PLAN_DETAIL);
    assingSelectedBenefit(benefit);
    if (benefit?.isInsurance) {
      navigate(INSURANCE_DETAIL_ROUTE);
    } else {
      navigate(BENEFIT_DETAIL_ROUTE);
    }
  }, [benefit, assingSelectedBenefit, navigate]);

  const getBenefitStatusInfo = (benefit: ICollaboratorBenefit) => {
    const isValidStatus = (status: string): status is keyof typeof BENEFIT_PROVIDER_STATUS_DATA =>
      status in BENEFIT_PROVIDER_STATUS_DATA;

    const validStatus = isValidStatus(benefit.status) ? benefit.status : null;

    if (!validStatus) {
      return null;
    }

    const statusData = BENEFIT_PROVIDER_STATUS_DATA[validStatus];

    if (benefit.isInsurance) {
      return statusData;
    }

    if (benefit.sku === 'marketcare' && benefit.status === 'active') {
      return {
        ...statusData,
        descriptionKey: (
          <Trans
            i18nKey={MARKETCARE_INSURANCE_DESCRIPTION_KEY}
            components={{
              u: (
                <a
                  className="bdm-description-link"
                  href={ACTIVATE_MARKETCARE_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              )
            }}
          />
        )
      };
    }

    if (benefit.status === 'active') {
      return BENEFIT_PROVIDER_STATUS_DATA['active'];
    }

    if (benefit.status === 'missing_data') {
      return BENEFIT_PROVIDER_STATUS_DATA['missing_data'];
    }

    return BENEFIT_PROVIDER_STATUS_DATA['in_process'];
  };

  const renderBenefitCard = useCallback((currentBenefit: ICollaboratorBenefit) => {
    const statusInfo = getBenefitStatusInfo(currentBenefit);

    if (statusInfo) {
      const { icon, tagVariant, descriptionKey } = statusInfo;
      setTagVariant(tagVariant as BdsLabelPillVariant);
      setStatusIcon(icon);
      setDescription(descriptionKey);
      setHasValidStatus(true);
    } else {
      setHasValidStatus(false);
    }
  }, []);

  const getInsuranceBenefitDetail = useCallback(async () => {
    try {
      setInsuranceDetail(undefined);
      setError(false);
      setLoading(true);
      const response = await Companies.getCollaboratorInsuranceDetail(
        collaborator.email,
        company.id,
        benefit.benefitId
      );
      setInsuranceDetail(response);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [benefit, collaborator, company]);

  const getInsuranceStartDate = () => {
    if (loading) {
      return <Skeleton variant="rectangular" className="bdm-skeleton" />;
    }
    if (error || !insuranceDetail || isEmptyObject(insuranceDetail)) {
      return (
        <div className="bdm-error-message">
          <img src={alertCircle} alt="Alert Icon" />
          <div className="bdm-error-text">{t(TRANSLATION_COLLABORATORS.detail.benefitDetail.loadError)}</div>
          <button className="bdm-error-link" onClick={getInsuranceBenefitDetail}>
            {t(TRANSLATION_COLLABORATORS.detail.benefitDetail.retry)}
          </button>
        </div>
      );
    }
    return <div className="bdm-text-bold">{insuranceDetail.startDate.replaceAll('-', '/')}</div>;
  };

  useEffect(() => {
    if (benefit) {
      renderBenefitCard(benefit);
    }
  }, [benefit, renderBenefitCard]);

  useEffect(() => {
    if (showStartDate && isOpen) {
      getInsuranceBenefitDetail();
    }
  }, [getInsuranceBenefitDetail, showStartDate, isOpen]);

  useEffect(() => {
    setInsuranceDetail(undefined);
    setError(false);
    setLoading(true);
  }, [isOpen]);

  return (
    <CustomModal open={isOpen} onClose={onClose} onBackdropClick={onClose}>
      <div className="bdm-container">
        <div className="bdm-header">
          <div className="bdm-collaborator-name">{collaborator.fullName || collaborator.email}</div>
          <div className="bdm-benefit-title">{benefit?.title}</div>
        </div>
        <div className="bdm-row">
          <div className="bdm-info-cotainer">
            {showStartDate && (
              <div className="bdm-coverage">
                <img className="bdm-coverage-icon" src={calendar} alt="Calendar Icon" />
                <div className="bdm-text">{t(TRANSLATION_COLLABORATORS.detail.benefitDetail.validityStart)}</div>
              </div>
            )}
            <div className="bdm-coverage">
              <img className="bdm-coverage-icon" src={BDS_PICTOGRAM_PATHS.insuranceSaveMoney} alt="Coverage Icon" />
              <div className="bdm-text">{t(TRANSLATION_COLLABORATORS.detail.benefitDetail.coverageMain)}</div>
            </div>
          </div>
          <div className="bdm-info-cotainer">
            {showStartDate && getInsuranceStartDate()}
            <button className="bdm-link" onClick={redirectToDetail}>
              {t(TRANSLATION_COLLABORATORS.detail.benefitDetail.seeDetails)}
            </button>
          </div>
        </div>

        {hasValidStatus && (
          <div className={`bdm-benefit-status ${benefit.status}`}>
            <BdsLabelPill text={t(benefit.statusKey).toUpperCase()} iconLeft={statusIcon} variant={tagVariant} />
            <div className="bdm-status-description">
              {React.isValidElement(description) ? description : t(description as string)}
            </div>
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default BenefitDetailModal;
